import { animated, useSpring, useTransition } from '@react-spring/web'
import throttle from 'lodash/throttle'
import React, { useEffect, useState } from 'react'
import { useMatch } from 'react-router-dom'

import { MainRouteName } from '@src/hooks/useAccountRouter'
import { useHasActiveOrders } from '@src/hooks/useHasActiveOrders'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { imageJitURL } from '@src/utils/imageJitURL'

import { BasketButton } from './BasketButton/BasketButton'
import { ServiceNavigation } from './ServiceNavigation/ServiceNavigation'
import {
  MarketplaceLogo,
  LogoContainer,
  RightIconsContainer,
  ServiceNavigationContainer,
  LogoAndBurgerButtonContainer,
  ActiveOrderDot,
  Container,
  MenuContainer,
  BurgerButton,
  BurgerMenuIcon,
} from './SiteHeader.styles'

import { useCheckoutRouter } from '@src/hooks/useCheckoutRouter/useCheckoutRouter'
import { AppBanner } from '../AppBanner/AppBanner'
import { useReactiveVar } from '@apollo/client'
import { showAppBannerVar } from './utils/reactiveVar'

export const SiteHeader: React.FC = () => {
  const { urlPath, ...marketplace } = useMarketplace()

  const { hasActiveOrders } = useHasActiveOrders()

  const { displayPermanentCheckout } = useCheckoutRouter()
  const showBanner = useReactiveVar(showAppBannerVar)

  const isOutletListRoute = useMatch(`/:deliveryZone?/:merchantCategory`)

  const isSegmentOutletListRoute = useMatch(
    '/segments/:segmentId/:segmentName/:deliveryZone?'
  )

  const isFeaturedOutletListRoute = useMatch('/:deliveryZone?/featured')

  const [scrollData, setScrollData] = useState({
    lastScrollY: 0,
    serviceNavigationIsVisible: true,
  })
  // track the scroll position so we can compare it to the last position to transition the ServiceNavigation
  // when scrolling up and down
  useEffect(() => {
    if (window.onscroll === null) {
      window.onscroll = throttle(
        () => {
          const newPosition = window.scrollY
          setScrollData({
            lastScrollY: newPosition,
            serviceNavigationIsVisible:
              newPosition < 200 || newPosition < scrollData.lastScrollY,
          })
        },
        250,
        { leading: true, trailing: true }
      )
    }

    return () => {
      window.onscroll = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const hideOnScrollSpring = useSpring({
    translateY: scrollData.serviceNavigationIsVisible ? '0' : '-100%',
  })
  const AnimatedServiceNavigationContainer = animated(
    ServiceNavigationContainer
  )

  const showServiceNavigation =
    isOutletListRoute || isSegmentOutletListRoute || isFeaturedOutletListRoute

  const appBannerTransitions = useTransition(true, {
    from: { top: '-60px' },
    enter: { top: '0' },
    config: { duration: 600 },
    reverse: false,
    delay: 4000,
  })

  const AnimatedContainer = animated(Container)

  const content = (
    <>
      <AppBanner />
      <MenuContainer>
        <LogoAndBurgerButtonContainer
          displayPermanentCheckout={displayPermanentCheckout}
        >
          <BurgerButton
            route={{ mainRouteName: MainRouteName.MENU }}
            displayPermanentCheckout={displayPermanentCheckout}
          >
            {hasActiveOrders && <ActiveOrderDot />}
            <BurgerMenuIcon id="profile" dataTestId="burger-menu-icon" />
          </BurgerButton>
          <LogoContainer to="/">
            <MarketplaceLogo
              role="img"
              aria-label={`${marketplace.name} logo`}
              imageUrl={imageJitURL(marketplace.logoImage, {
                resize: {
                  width: 200,
                  height: 100,
                  fit: 'outside',
                },
              })}
            />
          </LogoContainer>
          {!displayPermanentCheckout && (
            <RightIconsContainer>
              <BasketButton />
            </RightIconsContainer>
          )}
        </LogoAndBurgerButtonContainer>
      </MenuContainer>
      {showServiceNavigation && (
        <AnimatedServiceNavigationContainer
          displayPermanentCheckout={displayPermanentCheckout}
          showAppBanner={showBanner}
          style={hideOnScrollSpring}
        >
          <ServiceNavigation />
        </AnimatedServiceNavigationContainer>
      )}
    </>
  )

  if (showBanner) {
    return appBannerTransitions(styles => (
      <AnimatedContainer style={styles}>{content}</AnimatedContainer>
    ))
  }

  return <Container>{content}</Container>
}
